// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, OnInit, Input } from '@angular/core';
import { RolesService, RemoveItem, readParameterFromRouterState, RoleDetail, RemoveType } from '@app/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute } from '@angular/router';
import { map, switchMap, filter, tap, take } from 'rxjs/operators';
import { Subscription, Observable } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { State, selectRoleDetail } from '@app/access-management/state';

export enum RemoveResult {
  REMOVE_SUCCEEDED,
  REMOVE_FAILED,
  DISMISSED
}

@Component({
  selector: 'app-delete-modal',
  templateUrl: './delete-modal.component.html',
  styleUrls: ['./delete-modal.component.scss']
})

/**
 * The component name is a bit confused since it's coupled with Role-Detail component.
 * Because of short time constraint, no capacity to make it more generic.
 */
export class DeleteModalComponent implements OnInit {
  @Input() removeItem?: RemoveItem;
  private readonly roleId: string | undefined;
  private submitSubscription: Subscription | undefined = undefined;
  private readonly roleDetail$: Observable<RoleDetail>;

  constructor(
    public activatedRoute: ActivatedRoute,
    private roleService: RolesService,
    public activeModal: NgbActiveModal,
    private store: Store<State>) {
      this.roleId = readParameterFromRouterState(activatedRoute, 'id');
      this.roleDetail$ = this.store.pipe(select(selectRoleDetail(this.roleId ? this.roleId : '')),
        filter<RoleDetail>(roleDetail => roleDetail !== undefined));
  }

  ngOnInit() {
  }

  OnDestroy() {
    if (this.submitSubscription !== undefined) {
      this.submitSubscription.unsubscribe();
    }
  }

  close() {
    return this.activeModal.close(RemoveResult.DISMISSED);
  }

  delete() {
    if (this.roleId === undefined || this.removeItem === undefined) {
      throw Error(`Missing required arguments roleId: ${this.roleId} or remove item: ${this.removeItem}`);
    }

    this.submitSubscription = this.roleDetail$.pipe(
      map(detail => {
        return this.getUpdateRequest(detail, this.removeItem);
      }),
      take(1),
      switchMap(request => this.roleService.updateRoleDetail(this.roleId ? this.roleId : '', request))
    ).subscribe(
      _ => {
        this.activeModal.close(RemoveResult.REMOVE_SUCCEEDED);
      },
      err => {
        this.activeModal.close(RemoveResult.REMOVE_FAILED);
      }
    );
  }

  private getUpdateRequest(detail: RoleDetail, removeItem: any ) {
    if (removeItem !== undefined) {
      switch (removeItem.type) {
        case RemoveType.POLICY: {
          return {
            policyIds: detail.policies ?
            detail.policies.map(policy => policy.id).filter(id => id !== removeItem.data.id)
             : undefined
          };
        }
        case RemoveType.USER: {
          return {
            username: removeItem.data ? removeItem.data.username : undefined
          };
        }
        case RemoveType.ROLESCOPE: {
          return {
            grantId: removeItem.data ? removeItem.data.grant_id : undefined
          };
        }
        default: {
          return {};
        }
      }
    }
    return {};
  }

}



// WEBPACK FOOTER //
// ./src/app/access-management/roles/role-details/delete-modal/delete-modal.component.ts